import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GitCompareArrows, Loader2, ShieldCheck, ArrowRight, Check, Undo2, Eye, ListChecks } from "lucide-react";

// Admin-only FLAG-ONLY duplicate audit for the Category / Part Type lists. It never
// merges or changes anything — it surfaces likely duplicates and lets you mark a pair
// "reviewed" so it stops re-surfacing (until one of the two names changes).
export default function TaxonomyAuditPage() {
  const [data, setData] = useState(null);
  const [nmc, setNmc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewed, setShowReviewed] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/taxonomy/audit"); setData(data);
      const { data: c } = await api.get("/taxonomy/near-miss-candidates"); setNmc(c);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (path, p, msg) => {
    setBusyKey(p.pair_key);
    try {
      await api.post(`/taxonomy/audit/${path}`, { a_id: p.a.id, b_id: p.b.id, a_name: p.a.name, b_name: p.b.name });
      toast.success(msg);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  const Pair = ({ p, testid, reviewed }) => (
    <div data-testid={testid} className={`flex items-center gap-2 flex-wrap border rounded-xl p-3 ${reviewed ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}>
      <span className="font-medium text-slate-800">{p.a.name}</span>
      <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
      <span className="font-medium text-slate-800">{p.b.name}</span>
      {p.category_name && <span className="text-[11px] text-slate-400 font-mono">in {p.category_name}</span>}
      <Badge className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">{p.reason} · {Math.round(p.similarity * 100)}%</Badge>
      {reviewed ? (
        <Button data-testid={`audit-unreview-${p.pair_key}`} size="sm" variant="outline" disabled={busyKey === p.pair_key}
          onClick={() => act("undismiss", p, "Back in the audit")} className="rounded-lg h-8 text-xs ml-auto">
          {busyKey === p.pair_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Undo2 className="w-3.5 h-3.5 mr-1" />Un-review</>}
        </Button>
      ) : (
        <Button data-testid={`audit-review-${p.pair_key}`} size="sm" variant="outline" disabled={busyKey === p.pair_key}
          onClick={() => act("dismiss", p, "Marked reviewed")} className="rounded-lg h-8 text-xs ml-auto border-slate-300">
          {busyKey === p.pair_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Mark reviewed</>}
        </Button>
      )}
    </div>
  );

  const Section = ({ title, block, kind }) => (
    <div className="mb-6">
      <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">{title} <span className="text-slate-400 font-mono">({block?.count || 0})</span></h2>
      {(!block || block.count === 0) ? (
        <div data-testid={`audit-${kind}-clean`} className="flex items-center gap-2 text-sm text-emerald-600 border border-dashed border-emerald-200 rounded-xl p-4"><ShieldCheck className="w-4 h-4" />No new likely duplicates flagged.</div>
      ) : (
        <div className="space-y-2">{block.pairs.map((p, i) => <Pair key={p.pair_key} p={p} testid={`audit-${kind}-pair-${i}`} />)}</div>
      )}
      {showReviewed && block?.reviewed_count > 0 && (
        <div className="space-y-2 mt-2">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider">Reviewed ({block.reviewed_count})</div>
          {block.reviewed.map((p, i) => <Pair key={p.pair_key} p={p} testid={`audit-${kind}-reviewed-${i}`} reviewed />)}
        </div>
      )}
    </div>
  );

  const totalReviewed = (data?.categories?.reviewed_count || 0) + (data?.part_types?.reviewed_count || 0);

  return (
    <div data-testid="taxonomy-audit-page" className="max-w-3xl">
      <div className="flex items-center gap-2 mb-1"><GitCompareArrows className="w-6 h-6 text-[#B01E28]" /><h1 className="font-display text-2xl font-bold tracking-tight">List Duplicate Audit</h1></div>
      <p className="text-sm text-slate-500 mb-2">Likely-duplicate Category / Part Type entries, flagged for review. This <b>only flags</b> — it never merges or changes anything. Mark a pair "reviewed" to stop it re-surfacing; it comes back only if one of the two names changes.</p>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Button data-testid="audit-refresh" variant="outline" onClick={load} disabled={loading} className="rounded-lg h-9">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-run audit"}
        </Button>
        {totalReviewed > 0 && (
          <Button data-testid="audit-toggle-reviewed" variant={showReviewed ? "default" : "outline"} onClick={() => setShowReviewed((v) => !v)} className="rounded-lg h-9 text-xs">
            <Eye className="w-3.5 h-3.5 mr-1" />{showReviewed ? "Hide" : "Show"} reviewed ({totalReviewed})
          </Button>
        )}
        {data?.generated_at && <span className="text-[11px] text-slate-400 font-mono">as of {data.generated_at.replace("T", " ").slice(0, 16)}</span>}
      </div>

      {loading && !data ? (
        <div className="font-mono text-xs text-slate-400 py-10">RUNNING AUDIT…</div>
      ) : (
        <>
          <Section title="Categories" block={data?.categories} kind="categories" />
          <Section title="Part types" block={data?.part_types} kind="part-types" />

          {/* READ-ONLY floor confusion shortlist — ranks real dupe-confusions from the
              near-miss log so the future Brand merge admin knows which dupes matter most.
              Proposes only; merges nothing. */}
          <div data-testid="near-miss-candidates" className="mb-6 mt-8 border-t border-slate-200 pt-6">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks className="w-5 h-5 text-[#B01E28]" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Floor confusion shortlist <span className="text-slate-400 font-mono">({nmc?.candidates?.length || 0})</span></h2>
            </div>
            <p className="text-xs text-slate-500 mb-3">What staff actually typed vs. the entry they picked on the Identify screen — ranked by how often. A <b>preview shortlist</b> for the upcoming merge admin. This only ranks — it merges nothing yet.</p>
            {(!nmc || nmc.candidates.length === 0) ? (
              <div data-testid="nmc-empty" className="flex items-center gap-2 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-4"><ShieldCheck className="w-4 h-4 text-emerald-500" />No confusions logged yet — this fills in as staff use the Identify dropdowns.</div>
            ) : (
              <div className="space-y-2">
                {nmc.candidates.map((c, i) => (
                  <div key={`${c.field}-${c.canonical}`} data-testid={`nmc-row-${i}`} className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-xl p-3 bg-white">
                    <Badge className="rounded-full bg-slate-100 text-slate-500 border-0 text-[10px] font-mono uppercase">{c.field.replace("_", " ")}</Badge>
                    <span data-testid={`nmc-canonical-${i}`} className="font-semibold text-slate-900">{c.canonical}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {c.variants.map((v) => (
                        <span key={v.typed} data-testid={`nmc-variant-${i}-${v.typed}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                          {v.typed}<span className="font-mono text-[10px] text-amber-500">×{v.count}</span>
                        </span>
                      ))}
                    </div>
                    <Badge className="ml-auto rounded-full bg-red-50 text-[#B01E28] border border-red-200 text-[10px] font-mono">{c.total} hit{c.total === 1 ? "" : "s"}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
